import React, { useMemo, useState } from 'react';
import {
  BUDGET_FORMULA_STEPS,
  BUDGET_SLIDERS,
  DEFAULT_BUDGET_INPUTS,
  calculateBudgetMetrics,
  formatMoney,
  formatNumber,
  formatPercent,
  getInsightState,
  getRomiBarColor,
  getRomiBarWidth,
  getRomiStatusLabel,
  getSliderFillPercent,
} from './budgetPlannerLogic';
import { formatTrustedMessage } from '../../utils/htmlSecurity';
import './BudgetPlanner.css';

const formatSliderValue = (slider, value) => {
  const locale = 'ru-RU';

  if (slider.format === 'money') {
    return formatMoney(value, locale);
  }

  if (slider.format === 'percent') {
    return formatPercent(value);
  }

  return formatNumber(value, locale);
};

const SliderRow = ({ slider, value, onChange }) => {
  const fillPercent = getSliderFillPercent(value, slider.min, slider.max);

  return (
    <div className="bp-slider-row">
      <div className="bp-slider-top">
        <span className="bp-slider-label">{slider.label}</span>
        <span className="bp-slider-value">{formatSliderValue(slider, value)}</span>
      </div>
      <input
        type="range"
        className="bp-slider-input"
        min={slider.min}
        max={slider.max}
        step={slider.step}
        value={value}
        onChange={(event) => onChange(slider.id, Number(event.target.value))}
        style={{
          background: `linear-gradient(to right, var(--text) ${fillPercent}%, var(--surface2) ${fillPercent}%)`,
        }}
      />
      {slider.hintKey ? <p className="bp-field-hint">{slider.hint}</p> : null}
    </div>
  );
};

const ScenarioCard = ({ label, value, sub, locale }) => (
  <article className="bp-scenario">
    <div className="bp-scenario-label">{label}</div>
    <div className="bp-scenario-value">{formatMoney(value, locale)}</div>
    <div className="bp-scenario-sub">{sub}</div>
  </article>
);

const ResultMiniCard = ({ label, value, sub, explain }) => (
  <article className="bp-result-card">
    <div className="bp-result-label">{label}</div>
    <div className="bp-result-value">{value}</div>
    <div className="bp-result-sub">{sub}</div>
    {explain ? <p className="bp-mini-explain">{explain}</p> : null}
  </article>
);

const BudgetPlanner = () => {
  const [inputs, setInputs] = useState(DEFAULT_BUDGET_INPUTS);
  const locale = 'ru-RU';

  const metrics = useMemo(() => calculateBudgetMetrics(inputs), [inputs]);
  const insight = useMemo(
    () =>
      getInsightState({
        clients: inputs.clients,
        crPct: inputs.crPct,
        cpl: inputs.cpl,
        check: inputs.check,
        marginPct: inputs.marginPct,
        net: metrics.net,
        romi: metrics.romi,
        profit: metrics.profit,
        budget: metrics.budget,
      }),
    [inputs, metrics.budget, metrics.net, metrics.profit, metrics.romi],
  );

  const insightMessage = useMemo(
    () => formatTrustedMessage(insight.message, insight.vars || {}),
    [insight],
  );

  const handleInputChange = (field, value) => {
    setInputs((current) => ({
      ...current,
      [field]: value,
    }));
  };

  return (
    <div className="bp-planner">
      <div className="bp-main-layout">
        <section className="bp-card">
          <h2 className="bp-card-title">{'Параметры бизнеса'}</h2>
          {BUDGET_SLIDERS.map((slider) => (
            <React.Fragment key={slider.id}>
              <SliderRow
                slider={slider}
                value={inputs[slider.id]}
                onChange={handleInputChange}
              />
              {slider.dividerAfter ? <div className="bp-divider" /> : null}
            </React.Fragment>
          ))}

          <div className="bp-scenario-title">{'Сценарии бюджета (при вашей конверсии и цели)'}</div>
          <div className="bp-scenario-row">
            <ScenarioCard
              label={'Оптимист'}
              value={metrics.scenarios.opt}
              sub={'CPL дешевле на 30%'}
              locale={locale}
            />
            <ScenarioCard
              label={'База'}
              value={metrics.scenarios.base}
              sub={'ваш бюджет'}
              locale={locale}
            />
            <ScenarioCard
              label={'Пессимист'}
              value={metrics.scenarios.pes}
              sub={'CPL дороже на 40%'}
              locale={locale}
            />
          </div>
        </section>

        <section className="bp-results-col">
          <article className="bp-result-hero">
            <div className="bp-result-hero-label">{'Нужен бюджет в месяц'}</div>
            <div className="bp-result-hero-value">{formatMoney(metrics.budget, locale)}</div>
            <div className="bp-result-hero-sub">
              {`${metrics.leads} заявок × ${inputs.cpl} за заявку`}
            </div>
          </article>

          <div className="bp-results-mini">
            <ResultMiniCard
              label={'Заявок на 1 продажу'}
              value={formatNumber(metrics.leads, locale)}
              sub={`конверсия ${inputs.crPct}`}
              explain={'1 ÷ конверсию продаж. При 30% нужно 3.3 заявки чтобы закрыть одну продажу.'}
            />
            <ResultMiniCard
              label={'Выручка от рекламы'}
              value={formatMoney(metrics.revenue, locale)}
              sub={`${inputs.clients} клиентов × чек`}
              explain={'Деньги которые придут на счёт. Это ещё не прибыль — нужно вычесть себестоимость и рекламный бюджет.'}
            />
            <ResultMiniCard
              label={'Прибыль с рекламных клиентов'}
              value={formatMoney(metrics.profit, locale)}
              sub={'выручка × маржинальность'}
              explain={'Выручка после вычета себестоимости и расходов бизнеса. Из этой суммы ещё нужно оплатить рекламу.'}
            />
            <ResultMiniCard
              label={'Чистая прибыль от рекламы'}
              value={formatMoney(metrics.net, locale)}
              sub={'прибыль − рекламный бюджет'}
              explain={'То что остаётся после всех расходов включая рекламу. Главная цифра для оценки эффективности.'}
            />
          </div>

          <article className="bp-result-card bp-result-card--romi">
            <div className="bp-result-label">{'ROMI при вашем CPL'}</div>
            <div className="bp-romi-track">
              <div
                className="bp-romi-fill"
                style={{
                  width: `${getRomiBarWidth(metrics.romi)}%`,
                  background: getRomiBarColor(metrics.romi),
                }}
              />
            </div>
            <div className="bp-romi-footer">
              <div className="bp-result-value">{metrics.romi}%</div>
              <div className="bp-result-sub">{getRomiStatusLabel(metrics.romi)}</div>
            </div>
            <p className="bp-mini-explain">{'На каждый вложенный тенге в рекламу. 100% = вернули вдвое больше чем потратили.'}</p>
          </article>

          <div
            className={`bp-insight bp-insight--${insight.variant}`}
            dangerouslySetInnerHTML={{ __html: insightMessage }}
          />
        </section>
      </div>

      <section className="bp-formula-panel">
        <h2 className="bp-card-title">{'Как считается — шаг за шагом'}</h2>
        {BUDGET_FORMULA_STEPS.map((step) => (
          <article key={step.marker} className="bp-formula-step">
            <div className="bp-formula-step-num">{step.marker}</div>
            <div>
              <div className="bp-formula-step-title">{step.title}</div>
              <div className="bp-formula-step-formula">{step.formula}</div>
              <div
                className="bp-formula-step-example"
                dangerouslySetInnerHTML={{
                  __html: step.example,
                }}
              />
            </div>
          </article>
        ))}
      </section>

      <p className="bp-footer-note">{'Юнит-экономика на минималках · считайте до запуска рекламы'}</p>
    </div>
  );
};

export default BudgetPlanner;
